import * as Yup from "yup";
import { useMemo } from "react";
import { useLocStore } from "../../../localization";
import { LocContext } from "../../../localization/LocContext";

/**
 * Hook: `useApplyForJobSchema`
 * Builds a Yup validation schema for job‑application forms.
 *
 * @param requireCoverLetter – `true` if a cover‑letter text must be provided.
 * @param requireCv          – `true` if at least one attachment (e.g. CV) is mandatory.
 * @param requireConsent     – `true` (default) if the consent checkbox must be checked.
 *
 * @returns `{ APPLY_FOR_JOB_VALIDATION }` – a memoised Yup schema container.
 */
export const useApplyForJobSchema = (
  requireCoverLetter: boolean,
  requireCv: boolean,
  requireConsent: boolean = false
) => {
  // Wait for localisation strings before constructing the schema
  const L = useLocStore(LocContext.CV);
  if (!L) {
    return { APPLY_FOR_JOB_VALIDATION: null } as const;
  }

  const applicationTextErrorMessage = L.id("9ec84af715ab").loc(
    "Søknadstekst er påkrevd for å søke på denne stillingen."
  );
  const attachmentsErrorMessage = L.id("ab957f02be2e").loc(
    "Vedlegg med CV er påkrevd for å søke på denne stillingen."
  );
  const consentErrorMessage = L.id("0c810d4099de").loc(
    "Du må akseptere samtykkeerklæringen."
  );

  const APPLY_FOR_JOB_VALIDATION = useMemo(
    () =>
      Yup.object({
        applicationText: requireCoverLetter
          ? Yup.string().required(applicationTextErrorMessage)
          : Yup.string(),
        attachments: requireCv
          ? Yup.array()
              .of(Yup.mixed().required())
              .min(1, attachmentsErrorMessage)
          : Yup.array().of(Yup.mixed()),
        consent: requireConsent
          ? Yup.boolean()
              .oneOf([true], consentErrorMessage)
              .required(consentErrorMessage)
          : Yup.boolean().notRequired(),
      }),
    [requireCoverLetter, requireCv, requireConsent, L.changeIndicator]
  );

  return { APPLY_FOR_JOB_VALIDATION } as const;
};

/**
 * Shape of values expected by the Apply‑for‑Job form.
 */
export interface ApplyForJobFormValues {
  /** Cover‑letter text (may be optional if `requireCoverLetter` is `false`). */
  applicationText: string;
  /** Uploaded attachments (e.g. CV). */
  attachments: File[];
  /** Consent checkbox value – only present when required. */
  consent?: boolean;
}
